import 'rxjs/add/observable/of';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/filter';
import difference from 'lodash/difference';
import toNumber from 'lodash/toNumber';
import { Observable } from 'rxjs/Observable';
import log from '@atlassian/jira-common-util-logging/src/log';
import {
	ISSUE_CREATED_EVENT,
	ISSUE_UPDATED_EVENT,
} from '@atlassian/jira-realtime/src/common/constants/events.tsx';
import type { IssueEventPayload } from '@atlassian/jira-realtime/src/common/types/payloads.tsx';
import { fetchFilteredCardsService } from '../../../services/filtered-cards';
import { makeServiceContext } from '../../../services/service-context';
import { setAppData } from '../../../state/actions/app';
import { cardCreateFilteredV2 } from '../../../state/actions/cards/create';
import { refilterSuccess, refilterFailure } from '../../../state/actions/cards/filtered-cards';
import { REALTIME_DISPATCH_EVENT_BATCH } from '../../../state/actions/realtime/dispatch';
import { getAtlassianAccountId, getEpicIssueType } from '../../../state/selectors/app';
import type { ActionsObservable, Dependencies, MiddlewareAPI, State } from '../../../state/types';

// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
const isActiveTab = () => !document.hidden;

const getActiveCustomFilterIds = (state: State): string[] => state.ui.filters.CUSTOM_FILTER || [];
const isCustomFiltersActive = (state: State) => getActiveCustomFilterIds(state).length > 0;

export const refilterRequestEpic = (
	action$: ActionsObservable,
	store: MiddlewareAPI,
	dependencies?: Dependencies,
) =>
	action$
		.ofType(REALTIME_DISPATCH_EVENT_BATCH)
		.filter(isActiveTab)
		.filter(() => isCustomFiltersActive(store.getState()))
		.switchMap((action) => {
			const state = store.getState();
			const currentUser = getAtlassianAccountId(state);
			const triggerEvents = action.payload.events.filter(
				(event) =>
					event.payload.atlassianId === currentUser &&
					(event.type === ISSUE_CREATED_EVENT || event.type === ISSUE_UPDATED_EVENT),
			);
			if (triggerEvents.length > 0) {
				const customFilterIds = getActiveCustomFilterIds(state);

				const issueIds = triggerEvents.map(
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					(event) => (event.payload as IssueEventPayload).issueId,
				);
				return fetchFilteredCardsService(makeServiceContext(state), {
					customFilterIds,
					issueIds,
					epicIssueType: getEpicIssueType(state),
					storeFieldsActions: dependencies?.storeFieldsActions,
				})
					.mergeMap(({ filteredCardIds, data }) => {
						const excludedCardIds = difference(
							issueIds.map((issueId) => String(issueId)),
							filteredCardIds,
						);
						if (excludedCardIds.length) {
							// we only want to show the flag for created issues
							const excludedCreatedCardIds = excludedCardIds
								.map((cardId) => toNumber(cardId))
								.filter((cardId) =>
									triggerEvents.some(
										(event) =>
											// @ts-expect-error - TS2339 - Property 'issueId' does not exist on type 'EventPayload'.
											event.payload.issueId === cardId && event.type === ISSUE_CREATED_EVENT,
									),
								);
							if (excludedCreatedCardIds.length) {
								return [
									...(data ? [setAppData(data)] : []),
									cardCreateFilteredV2(excludedCreatedCardIds),
									refilterSuccess(excludedCardIds, filteredCardIds),
								];
							}
						}

						return [
							...(data ? [setAppData(data)] : []),
							refilterSuccess(excludedCardIds, filteredCardIds),
						];
					})
					.catch((error) => {
						log.safeErrorWithoutCustomerData(
							'backlog.filtered-cards.refilter.batch.failure',
							'Fail to batch re-filter cards with custom filters',
							error,
						);

						return Observable.of(refilterFailure(error));
					});
			}
			return Observable.empty<never>();
		});
